/**
 * Sign in, and change a password when one is due.
 *
 * The API side of authentication landed before any way to reach it: there was
 * no password field anywhere in the front end and nothing called `/auth/login`.
 * A deployed environment therefore had a working session API and no way for a
 * person to obtain a session.
 *
 * This screen renders instead of the shell when `/api/session` answers 401, so
 * it is reached by not being signed in rather than by navigating to a route.
 * There is no `/login` path to bookmark, and nothing to link to: the shell
 * decides, which keeps "am I signed in" in one place.
 */
import { useState, type FormEvent } from "react";
import { ApiClientError, authRequest } from "../lib/api";
import { MIN_PASSWORD_LENGTH, type LoginResponse } from "@truckbuddy/contract";
import { BrandMark } from "../components/BrandMark";
import { useSessionState } from "../lib/queries";

interface SignInProps {
  /** Called after a successful sign-in, and after a required password change. */
  onSignedIn: () => void;
  /**
   * Open straight on the change-password step, for a session that already
   * exists and is still carrying `mustChangePassword`.
   *
   * Without this the requirement lasted exactly one page load. The flag came
   * back on the LOGIN response only and was acted on here, so a refresh — or
   * closing the tab and returning while the cookie was still live — walked past
   * it into a fully working app.
   */
  forcePasswordChange?: boolean;
}

/**
 * The server's own minimum. Stated here so the form can say so before a
 * round-trip, and deliberately NOT enforced on the sign-in field: an existing
 * password that predates a policy change must still be able to sign in, and a
 * length rule on the sign-in form would lock those accounts out while telling
 * an attacker what the policy is.
 */
const MIN_NEW_PASSWORD = MIN_PASSWORD_LENGTH;

export function SignIn({ onSignedIn, forcePasswordChange = false }: SignInProps) {
  // Offered only where a reset link can actually be delivered. The probe is
  // already cached by the shell, so this is not another request; strictly `true`
  // so a probe that is still loading, or failed, shows no dead-end link.
  const canResetPassword = useSessionState().data?.passwordResetAvailable === true;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Set when the server says this account must choose a new password before it
  // can do anything. The session cookie already exists at that point, which is
  // what lets the change be authenticated.
  const [mustChange, setMustChange] = useState(forcePasswordChange);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  async function submitSignIn(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await authRequest<LoginResponse>("/auth/login", { email, password });
      if (result.mustChangePassword) {
        setMustChange(true);
        setBusy(false);
        return;
      }
      onSignedIn();
    } catch (caught) {
      // The server returns one message for every credential failure -- unknown
      // address, wrong password, deactivated account -- so echoing it verbatim
      // preserves that rather than inventing a more specific one here.
      setError(
        caught instanceof ApiClientError ? caught.message : "Could not sign in. Try again.",
      );
      setBusy(false);
    }
  }

  async function submitNewPassword(event: FormEvent) {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("The two new passwords do not match.");
      return;
    }
    if (newPassword.length < MIN_NEW_PASSWORD) {
      setError(`Choose at least ${MIN_NEW_PASSWORD} characters.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await authRequest("/auth/change-password", {
        currentPassword: password,
        newPassword,
      });
      // Changing a password revokes every session for the account, including
      // the one that just made this request. Signing in again with the new
      // password is the only way forward, so the form resets to that rather
      // than pretending the old session survived.
      setMustChange(false);
      setPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setBusy(false);
      setError("Password changed. Sign in with your new password.");
    } catch (caught) {
      setError(
        caught instanceof ApiClientError ? caught.message : "Could not change the password.",
      );
      setBusy(false);
    }
  }

  return (
    <div className="screen-body" style={{ maxWidth: 380, margin: "0 auto", paddingTop: "12vh" }}>
      <div className="panel stack">
        <div className="nav-brand" style={{ marginBottom: "var(--space-2)" }}>
          <BrandMark />
        </div>

        {mustChange ? (
          <form className="stack" onSubmit={submitNewPassword}>
            <p className="text-muted">
              This account needs a new password before it can be used.
            </p>
            {/*
              Only on the RESUMED path. Arriving here straight from the sign-in
              form, `password` already holds what was just typed and asking for
              it again would be pointless. Arriving here from a live session —
              a refresh, or a new tab — it is empty, and
              `/auth/change-password` verifies the current password before it
              will set a new one.

              Without this field that path could not complete at all: the
              request went out with an empty `currentPassword`, failed
              `changePasswordBody` on its `min(1)`, and came back as the API's
              generic 400 — which reads "New password must be at least 15
              characters" and sends you looking at the wrong field.
            */}
            {forcePasswordChange ? (
              <label className="stack">
                <span className="text-muted">Current password</span>
                <input
                  maxLength={200}
                  className="input"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </label>
            ) : null}
            <label className="stack">
              <span className="text-muted">New password</span>
              <input
                maxLength={200}
                className="input"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </label>
            <label className="stack">
              <span className="text-muted">Confirm new password</span>
              <input
                maxLength={200}
                className="input"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </label>
            <p className="text-muted">At least {MIN_NEW_PASSWORD} characters.</p>
            {error ? <p role="alert">{error}</p> : null}
            <button className="btn btn-primary" type="submit" disabled={busy}>
              {busy ? "Saving…" : "Set password"}
            </button>
          </form>
        ) : (
          <form className="stack" onSubmit={submitSignIn}>
            <label className="stack">
              <span className="text-muted">Email</span>
              <input
                maxLength={320}
                className="input"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </label>
            <label className="stack">
              <span className="text-muted">Password</span>
              <input
                maxLength={200}
                className="input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>
            {error ? <p role="alert">{error}</p> : null}
            <button className="btn btn-primary" type="submit" disabled={busy}>
              {busy ? "Signing in…" : "Sign in"}
            </button>
            {canResetPassword ? (
              <a href="/forgot-password" className="text-muted">
                Forgot your password?
              </a>
            ) : null}
          </form>
        )}
      </div>
    </div>
  );
}
